import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	completeUserAccountMeCompleteAccountPutMutation,
	getCurrentLegalVersionLegalCurrentVersionGetOptions,
	readUsersMeUsersMeGetQueryKey,
	updateTosAcceptanceMeTosAcceptancePutMutation,
} from '@/client/@tanstack/react-query.gen';
import { User } from '@/client/types.gen';
import { isTermsAcceptanceRequired } from '@/utils/legal/semverUtils';

interface TermsAcceptanceData {
	tosAccepted: boolean;
	marketingAccepted?: boolean;
}

type AcceptedTermsState = {
	tos_version: string;
	tos_agreed_at: string;
	marketing_consent?: boolean;
	marketing_consent_at?: string | null;
};

export const mergeAcceptedTermsIntoUser = (user: User | undefined, accepted: AcceptedTermsState) =>
	user
		? {
				...user,
				tos_version: accepted.tos_version,
				tos_agreed_at: accepted.tos_agreed_at,
				...(accepted.marketing_consent !== undefined && {
					marketing_consent: accepted.marketing_consent,
					marketing_consent_at: accepted.marketing_consent_at,
				}),
			}
		: user;

/**
 * Hook for managing terms and services compliance
 *
 * Automatically detects if user needs first-time acceptance or version update
 * Uses callback for navigation to decouple from useNavigate()
 */
export const useTermsCompliance = (user?: User | null, onNavigateAfterAcceptance?: () => void) => {
	const queryClient = useQueryClient();
	const [isLoading, setIsLoading] = useState(false);

	// Fetch current legal version
	const { data: legalVersionData, isLoading: isLegalVersionLoading } = useQuery({
		...getCurrentLegalVersionLegalCurrentVersionGetOptions(),
		enabled: !!user,
		staleTime: 60 * 60 * 1000,
		gcTime: 24 * 60 * 60 * 1000,
	});

	const currentLegalVersion = legalVersionData?.current_version;

	// Determine if acceptance is required and what type.
	// Gate every branch on `user` being loaded: an undefined user (initial load, or the token-refresh
	// window where readUsersMe is briefly disabled) must NOT be read as "never accepted". Otherwise,
	// because the legal-version query stays cached (staleTime 1h / gcTime 24h), the observer sees
	// requiresAcceptance && currentLegalVersion and fires a spurious TOS redirect for a compliant user.
	const isFirstTimeAcceptance = !!user && !user.tos_version;
	const requiresAcceptance = Boolean(
		user &&
		(!user.tos_version ||
			(currentLegalVersion && isTermsAcceptanceRequired(user.tos_version, currentLegalVersion.version))),
	);

	// Note: Auto-redirect logic is now handled by TermsComplianceObserver component

	// Intentionally do not invalidate /me after acceptance. The mutation response is authoritative
	// for TOS/marketing fields, and an immediate refetch can return stale acceptance data or no-op
	// while the query is disabled. Other user fields reconcile on the next normal /me refetch.

	// Update mutation for existing users (version changes)
	const updateTermsMutation = useMutation({
		...updateTosAcceptanceMeTosAcceptancePutMutation(),
		onSuccess: (response) => {
			// The mutation has already confirmed these values. Update /me synchronously so every
			// compliance guard sees them before navigation, including while /me is temporarily disabled.
			queryClient.setQueryData<User>(readUsersMeUsersMeGetQueryKey(), (cachedUser) =>
				mergeAcceptedTermsIntoUser(cachedUser, response),
			);

			onNavigateAfterAcceptance?.();
		},
	});

	// Complete mutation for first-time users
	const completeTermsMutation = useMutation({
		...completeUserAccountMeCompleteAccountPutMutation(),
		onSuccess: (response) => {
			queryClient.setQueryData<User>(readUsersMeUsersMeGetQueryKey(), (cachedUser) =>
				mergeAcceptedTermsIntoUser(cachedUser, response),
			);

			onNavigateAfterAcceptance?.();
		},
	});

	// Unified accept handler
	const handleAcceptTerms = async (data: TermsAcceptanceData) => {
		if (!currentLegalVersion || !data.tosAccepted) return;

		setIsLoading(true);
		try {
			if (isFirstTimeAcceptance) {
				// Complete new user's account with TOS and marketing
				await completeTermsMutation.mutateAsync({
					body: {
						tos_version: currentLegalVersion.version,
						marketing_consent: data.marketingAccepted,
					},
				});
			} else {
				// Update existing user's TOS version (no marketing consent)
				await updateTermsMutation.mutateAsync({
					body: {
						tos_version: currentLegalVersion.version,
					},
				});
			}
		} finally {
			setIsLoading(false);
		}
	};

	const isCompliant = !requiresAcceptance;
	const totalIsLoading =
		isLegalVersionLoading || isLoading || updateTermsMutation.isPending || completeTermsMutation.isPending;

	return {
		// Status
		isCompliant,
		isLoading: totalIsLoading,
		requiresAcceptance,
		isFirstTimeAcceptance,

		// Data
		currentLegalVersion,

		user,

		// Actions
		handleAcceptTerms,
	};
};
